/**
 * faq-content.ts — the Help & FAQ copy, as typed data, plus the role resolver.
 *
 * Prototype of record: `design_handoff_help_faq` (Claude Design handoff,
 * 2026-08-25). Behaviour reference: `faqGroupsFor()` in
 * `design/help-faq-data.jsx`.
 *
 * Two rules govern this module, and they are easy to conflate:
 *
 *   1. **Identity is the authored `id`.** React keys, `aria-controls` pairs and
 *      open-state tracking all come from `id` and never from `question`. See
 *      `docs/solutions/ui-bugs/timesheet-grid-groups-by-non-unique-project-name-2026-05-10.md`
 *      for what using a display string as a key cost us last time.
 *   2. **Dedupe compares `question` text.** A question authored in two tiers
 *      renders once, at the highest tier the reader can see. That is a copy
 *      concern, not an identity concern, and the two must not be merged.
 *
 * Copy fidelity vs. copy truth: the prototype's page chrome is authoritative,
 * but its FAQ answers were commissioned as plausible examples and several
 * contradicted the shipping UI. Every answer below names a control, status value
 * and navigation path that exists in the app today. Two prototype answers
 * described features the app does not have — per-row blocking/advisory warnings
 * on the contributor grid, and an approver-side reminder — and were deleted
 * rather than softened, per the origin's rule that stale help text is worse than
 * none. The corrections are recorded in the plan's Documented Divergences table.
 *
 * **Content owner: the maintainer. Re-verify this file in any PR that renames a
 * user-facing control.**
 */
import type { Role } from '@time-tracking/shared';

/** An inline run of answer text. A bare string is plain prose. */
export type FaqSpan = string | { readonly strong: string };

/** One term/description pair in an answer's definition list. */
export interface FaqDefinition {
  readonly term: string;
  readonly description: string;
}

/**
 * A block of answer body. Kept as data rather than JSX so the copy stays in a
 * `.ts` module and `FaqAccordion` owns all the markup.
 */
export type FaqAnswerBlock =
  | { readonly kind: 'paragraph'; readonly spans: readonly FaqSpan[] }
  | { readonly kind: 'hint'; readonly spans: readonly FaqSpan[] }
  | { readonly kind: 'definitionList'; readonly items: readonly FaqDefinition[] };

export interface FaqEntry {
  /**
   * Stable, authored identity. Never derived from `question` — questions are
   * display strings, and two of them may legitimately collide.
   */
  readonly id: string;
  readonly question: string;
  readonly answer: readonly FaqAnswerBlock[];
  /** The lowest role that should see this answer. Roles nest; see FAQ_TIER_ORDER. */
  readonly tier: Role;
}

export interface FaqGroup {
  readonly tier: Role;
  readonly label: string;
  readonly entries: readonly FaqEntry[];
}

/** Group label bar copy, from the handoff. */
export const FAQ_TIER_LABELS: Readonly<Record<Role, string>> = {
  administrator: 'Administering the app',
  approver: 'Approving your team',
  contributor: 'Entering your own time',
};

/**
 * Which tiers each role sees, highest first.
 *
 * Roles nest: an approver is also a contributor, an administrator is also both.
 * The order here is the render order of the group label bars.
 */
const FAQ_TIER_ORDER: Readonly<Record<Role, readonly Role[]>> = {
  contributor: ['contributor'],
  approver: ['approver', 'contributor'],
  administrator: ['administrator', 'approver', 'contributor'],
};

const FAQ_ENTRY_LIST: readonly FaqEntry[] = [
  // ── Entering your own time ───────────────────────────────────────────────
  {
    id: 'contributor-submit-week',
    tier: 'contributor',
    question: 'How do I submit my week?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Open ',
          { strong: 'My timesheet' },
          ', enter hours on each line, then choose ',
          { strong: 'Submit for approval' },
          '.',
        ],
      },
      {
        // Corrected: the prototype said the button waits on a "blocking
        // warning". It waits on the weekly hours target — Timesheet.tsx:804,835.
        kind: 'paragraph',
        spans: [
          'The button stays unavailable until the week reaches your weekly hours target; the bar above it shows how many hours are still needed.',
        ],
      },
      {
        kind: 'paragraph',
        spans: ["Once submitted, the week is read-only and moves to your team's approval queue."],
      },
    ],
  },
  {
    id: 'contributor-after-approval',
    tier: 'contributor',
    question: 'What happens after my timesheet is approved?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          "The week's status changes to ",
          { strong: 'Approved' },
          " and the hours count toward the department's reporting for that period.",
        ],
      },
      {
        // Corrected twice. Post-approval recall shipped and does not need the
        // approver (repositories/timesheets.ts:926) — but recall is REJECTED once
        // the reporting period is closed: `isWeekLocked` throws 409 "This
        // reporting period is closed. Recall is not allowed."
        // (repositories/timesheets.ts:930-934). The unqualified "Yes" promised an
        // action that fails, which is the shape the origin's rule exists to stop.
        kind: 'paragraph',
        spans: [
          'If something needs to change afterwards, choose ',
          { strong: 'Recall' },
          ' on that week, while its reporting period is still open. It becomes editable again and has to be submitted and approved a second time. Once the period closes, recall is refused — ask an administrator.',
        ],
      },
    ],
  },
  {
    id: 'contributor-assignments',
    tier: 'contributor',
    question: 'Who changes my project assignment?',
    answer: [
      {
        // Corrected: the prototype named only the approver and administrators.
        // A project lead can assign too — project-access.ts:38-46.
        kind: 'paragraph',
        spans: [
          "An administrator, an approver on the team that owns the project, or the project's lead manages assignments. You cannot add or remove your own.",
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          "If a project you are working on isn't in your list, ask one of them to assign you to it.",
        ],
      },
      {
        // From the Timesheets Application Guide (Oct 2026): parking hours on a
        // near-miss task is the failure the guide calls out.
        kind: 'hint',
        spans: [
          "Don't record the time to an unrelated task while you wait — the hours would be reported against the wrong work.",
        ],
      },
    ],
  },
  {
    id: 'contributor-statuses',
    tier: 'contributor',
    question: 'What do the statuses mean?',
    answer: [
      {
        // Corrected: the prototype listed six statuses. `TimesheetStatus` has
        // four, and the contributor's own period bar labels an un-submitted
        // week "Open", not "Draft" — PeriodStatusPill.tsx:30-35. A closed
        // reporting period is a separate concept, not a fifth status.
        kind: 'definitionList',
        items: [
          { term: 'Open', description: 'The week is yours to edit. Nothing has been sent.' },
          {
            term: 'Submitted',
            description:
              "In your approver's queue. Read-only until they act, or until you recall it.",
          },
          { term: 'Approved', description: "Accepted and counted toward the period's reporting." },
          {
            term: 'Returned',
            description: 'Sent back with a comment. Editable again, and it needs resubmitting.',
          },
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Once a reporting period closes, its weeks are locked for everyone: no submitting, editing or recalling, and no approving, returning or unapproving. An administrator can reopen the period if a change is needed.',
        ],
      },
    ],
  },
  {
    id: 'contributor-edit-after-submit',
    tier: 'contributor',
    question: 'Can I still edit a week after I submit it?',
    answer: [
      {
        // Corrected: the prototype said "Yes, until it is approved" and named a
        // "Recall sheet" button. Recall renders on submitted *and* approved
        // weeks, and the label is "Recall" — Timesheet.tsx:842-851.
        kind: 'paragraph',
        spans: [
          'Yes, while the week\u2019s reporting period is still open. Choose ',
          { strong: 'Recall' },
          ' on the week — submitted or already approved — make the change, and submit again. After the period closes, recall is refused and an administrator has to reopen it.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'A recalled week goes back to ',
          { strong: 'Open' },
          ', so it needs approving again once you resubmit it.',
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026), "Time Classifications".
    // The labels match the type tags the app renders — categoryType.ts.
    id: 'contributor-types',
    tier: 'contributor',
    question: 'What do Direct, Indirect and Leave mean?',
    answer: [
      {
        kind: 'definitionList',
        items: [
          {
            term: 'Direct',
            description:
              'Work for a specific service or time-limited initiative. Covers both project work (delivering a defined outcome) and sustainment (operating, supporting and improving an existing service). Nearly all project tasks are Direct.',
          },
          {
            term: 'Indirect',
            description:
              'Work that supports you or Campus Solutions generally and cannot be tied to one service or project, such as Gen Admin or Professional Development.',
          },
          {
            term: 'Leave',
            description:
              'Approved time away — sick leave, vacation, comp time, holidays and other approved absences — so your full week is accounted for.',
          },
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026), "Centrally Managed
    // General Activities". Section heading matches Timesheet.tsx.
    id: 'contributor-general-activities',
    tier: 'contributor',
    question: 'Which General Activity should I use?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'The lines under ',
          { strong: 'Campus Solutions – General Activities' },
          ' are managed centrally. Leave and Gen Admin appear on everyone’s timesheet; for most of the others, your team lead chooses who gets them, so you may not see every activity listed here. Use them only when the work is not for a specific service or project.',
        ],
      },
      {
        kind: 'definitionList',
        items: [
          {
            term: 'Leave',
            description: 'Sick leave, vacation, comp time, holidays and other approved absences.',
          },
          {
            term: 'Gen Admin',
            description:
              'Department and team meetings, regular meetings with your supervisor, status and time reporting, required forms and routine internal coordination.',
          },
          {
            term: 'Professional Development',
            description:
              'Courses, conferences, workshops, certifications, professional reading and communities of practice that build your skills.',
          },
          {
            term: 'Governance',
            description:
              'Councils, steering committees, architecture or standards reviews, policy development and risk or compliance review that serve Campus Solutions broadly.',
          },
          {
            term: 'Communications',
            description:
              'Division-wide news, staff updates, newsletters, recognition content and communication calendars.',
          },
          {
            term: 'CSOL Operations',
            description:
              'Division-wide budget, resource and capacity planning, organizational reporting and metrics, and operational process improvement.',
          },
          {
            term: 'ISO Score Cards & Vulnerabilities',
            description:
              'Reviewing, coordinating and reporting on Information Security Office scorecard results across teams.',
          },
          {
            term: 'ISORA',
            description:
              'Coordinating the annual Information Security Office Risk Assessment across teams.',
          },
          {
            term: 'Personnel Management',
            description:
              'Supervisory work — one-on-ones, coaching, performance reviews, hiring and onboarding. Only approvers have this line.',
          },
        ],
      },
      {
        kind: 'hint',
        spans: [
          'If a meeting, training or communication is about a specific service or project, record it to that project’s Direct task instead. The same goes for technical fixes that follow a security finding.',
        ],
      },
    ],
  },
  {
    // Lead-assigned categories (plan 2026-10-28-001). A granted category is
    // seeded the next time the person opens a week they haven't submitted.
    id: 'contributor-general-activity-missing',
    tier: 'contributor',
    question: 'A General Activity I need isn’t on my timesheet. What do I do?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Ask your team lead. Leads choose who on their team gets each General Activity, and once they add you it appears the next time you open a week you haven’t submitted.',
        ],
      },
    ],
  },
  {
    // Lead-assigned categories: the not_granted badge (Timesheet.tsx
    // RESTRICTED_BADGE_LABEL) and the ratchet — reductions save, increases don't.
    id: 'contributor-no-longer-given',
    tier: 'contributor',
    question: 'Why does a General Activity say “No longer given”?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Your lead removed that activity after you had already recorded time or a note on it. Your hours stay on the sheet and are approved as normal, but the line takes no new hours: you can lower it, not raise it.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'If you still need it, ask your lead to give it back. The label goes away as soon as they do.',
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026), "Project Ownership and
    // Approvers". Approval authority follows team membership.
    id: 'contributor-who-approves',
    tier: 'contributor',
    question: 'Who approves my timesheet if I work on another team’s project?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Your own team’s approver. Approval follows the team you belong to, not the team that owns the project, so your whole week goes to one queue however many teams’ projects it touches.',
        ],
      },
    ],
  },

  // ── Approving your team ─────────────────────────────────────────────────
  {
    id: 'approver-approve-team',
    tier: 'approver',
    question: "How do I approve my team's timesheets?",
    answer: [
      {
        // Corrected: the drawer's own buttons read "Approve sheet" and "Return
        // for fix" — DetailDrawer.tsx:592,628.
        kind: 'paragraph',
        spans: [
          'Open ',
          { strong: 'Approvals' },
          '. Submitted sheets are listed with their weekly totals; open one to review it line by line, then choose ',
          { strong: 'Approve sheet' },
          ' or ',
          { strong: 'Return for fix' },
          '.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'The inbox rows carry the same ',
          { strong: 'Approve' },
          ' and ',
          { strong: 'Return' },
          ' actions, so a clean week can be approved without opening it.',
        ],
      },
    ],
  },
  {
    id: 'approver-return',
    tier: 'approver',
    question: 'What happens when I return a timesheet?',
    answer: [
      {
        // Corrected: the prototype's status label ("Returned · needs changes")
        // and its per-line approver notes do not exist. Returning requires a
        // comment, which is what the submitter sees — ReturnDialog.tsx:43,93.
        //
        // A closed period locks return as well as editing (decided 2026-10-19,
        // #65 — `assertWeekNotLocked` in approvals.ts), so the earlier "returning
        // is not blocked" caveat no longer applies.
        kind: 'paragraph',
        spans: [
          'The week becomes ',
          { strong: 'Returned' },
          ' and is editable again for the submitter. You can only return a week while its reporting period is open — a closed period locks the week for everyone until an administrator reopens it.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'A return needs a comment, and that comment is shown to them on their timesheet. The week reappears in your queue once they resubmit.',
        ],
      },
    ],
  },
  {
    id: 'approver-correct-hours',
    tier: 'approver',
    question: "Can I correct someone's hours myself?",
    answer: [
      {
        kind: 'paragraph',
        spans: [
          "No. Nobody enters hours on another person's sheet. Return the week with a comment describing what needs to change, and they correct and resubmit.",
        ],
      },
      {
        kind: 'hint',
        spans: [
          'This is deliberate: it keeps every hour attributable to the person who worked it.',
        ],
      },
    ],
  },
  {
    id: 'approver-undo-approval',
    tier: 'approver',
    question: 'Can I undo an approval?',
    answer: [
      {
        // Corrected: the affordance is "Unapprove", and it produces `returned`,
        // not `submitted` — approvals.ts:822 and REQUIREMENTS.md M-06.
        //
        // The prototype's "an administrator has to reopen it" is right again: as
        // of 2026-10-19 (#65) a closed period gates approve, return and unapprove
        // for everyone, administrators included — `assertWeekNotLocked` in
        // approvals.ts — and the week view withholds the buttons.
        kind: 'paragraph',
        spans: [
          'Yes. Choose ',
          { strong: 'Unapprove' },
          ' on the approved week and leave a comment. The week becomes ',
          { strong: 'Returned for fix' },
          ' and comes back to your queue when they resubmit. Once the reporting period has closed, the week is locked for everyone and Unapprove is not offered — an administrator has to reopen the period first.',
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Unapproving does not put the week straight back to Submitted; it goes to the submitter.',
        ],
      },
    ],
  },
  {
    id: 'approver-outstanding',
    tier: 'approver',
    question: "Who on my team hasn't submitted yet?",
    answer: [
      {
        // Repointed from the By-week "Missing" filter to the Needs action view,
        // which is now where Approvals lands. The old answer described a single
        // week's roster; this view spans every open period, which is the actual
        // answer to "who hasn't submitted yet".
        kind: 'paragraph',
        spans: [
          'Open ',
          { strong: 'Approvals' },
          '. ',
          { strong: 'Needs action' },
          ' is where it lands, and it spans every open period rather than one week at a time. Filter to ',
          { strong: 'Not submitted' },
          ' for the people who still owe you a week — both the weeks somebody started and left in draft, and the weeks nobody has opened at all.',
        ],
      },
      {
        // Corrected: the prototype promised a reminder from the same place.
        // Reminders are deferred and on hold (REQUIREMENTS.md U-08), and the
        // stub was removed — so the reminder half is deleted, not softened.
        // Stated outright rather than omitted: an approver who assumes the app
        // is nudging people will wait instead of asking. Matches the wording of
        // the view's own footnote.
        kind: 'hint',
        spans: [
          'The app does not send reminders. Unsubmitted weeks are listed for visibility, so following one up is a conversation you start yourself.',
        ],
      },
    ],
  },

  {
    // Source: Timesheets Application Guide (Oct 2026), "Instructions to create a
    // project, task, and assignment". The guide also says a project lead can
    // create projects; post-project.ts:27 allows only administrators and the
    // owning team's approvers, so that claim is not repeated here.
    id: 'approver-create-project',
    tier: 'approver',
    question: 'How do I set up a project and put it on people’s timesheets?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'First check whether an existing project and task already covers the work — reuse it if so. Otherwise open ',
          { strong: 'Projects & tasks' },
          ', choose ',
          { strong: 'New project' },
          ', and give it a name, a ',
          { strong: 'Code' },
          ', the team that will own it and a ',
          { strong: 'Short description' },
          '. Choose ',
          { strong: 'Create project' },
          '.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'On the ',
          { strong: 'Members' },
          ' tab, use ',
          { strong: '+ Add lead…' },
          ' for anyone who should help maintain it, and ',
          { strong: '+ Add team…' },
          ' for each team whose members will work on it. On the ',
          { strong: 'Tasks' },
          ' tab, add each task and pick its type, then assign it with ',
          { strong: 'Assign teams to this task' },
          ' or ',
          { strong: 'Add person to this task' },
          ' on the task’s row. Assigned tasks appear on those people’s timesheets.',
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Start the code with your team’s acronym, e.g. IAM-IGA-P3, or CS- for a division-wide effort. Only administrators and approvers on the owning team can create a project.',
        ],
      },
    ],
  },
  {
    id: 'approver-remove-project',
    tier: 'approver',
    question: 'How do I remove a project I created by mistake?',
    answer: [
      {
        // The verb divergence is the whole point of this entry: managers asked
        // for "delete", the app ships "Archive…", and nothing else in-product
        // explains why the word changed or that it is reversible.
        kind: 'paragraph',
        spans: [
          'Open the project and choose ',
          { strong: 'Archive…' },
          '. There is no delete: archiving stops the project seeding rows onto anyone’s timesheet and moves it to the ',
          { strong: 'Archived' },
          ' group in the sidebar, while every hour already logged is kept and still counted in reports.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'It is reversible. Expand ',
          { strong: 'Archived' },
          ', open the project and choose ',
          { strong: 'Restore…' },
          '. The staffing recorded when it was archived goes back into effect, minus anyone removed from a team since.',
        ],
      },
      {
        kind: 'hint',
        spans: [
          'The project code is freed for reuse only if you tick that box in the archive dialog — and restoring does not take the old code back.',
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026). "Leave is admin-only" is
    // department policy, not enforced by the type picker — worded as policy.
    id: 'approver-task-type',
    tier: 'approver',
    question: 'Which type should a new task get?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          { strong: 'Direct' },
          ', almost always — it is the default, and covers both project work and sustainment of a service.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'Choose ',
          { strong: 'Indirect' },
          ' only when no General Activity already covers the work and it needs tracking separately. Leave is managed by an administrator in General Activities and should not be used on project tasks.',
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Don’t recreate General Activities as project tasks. You choose who on your team gets each one in ',
          { strong: 'Projects & tasks → General activities' },
          ', and an administrator can add a new one if your team needs it.',
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026). Matches the API: lead
    // status grants project access (project-access.ts), never approval.
    id: 'approver-lead-vs-approver',
    tier: 'approver',
    question: 'Is a project lead also an approver?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'No. A project lead maintains a project’s tasks and assignments, but can approve timesheets only if they are also an approver on that team. Approval follows team membership, not project ownership.',
        ],
      },
    ],
  },
  {
    // Source: Timesheets Application Guide (Oct 2026), "Access to the
    // Timesheets Application". The roster syncs from AD (REQUIREMENTS.md I-08).
    id: 'approver-new-hire-access',
    tier: 'approver',
    question: 'A new team member can’t sign in. What do I do?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Access comes from the Campus Solutions group in Azure AD, which people are normally added to during onboarding. The roster syncs nightly, so they can sign in the day after they are added. Once they are on your team they get Leave, Gen Admin and every General Activity your team has turned on for everyone, and you can assign project tasks.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'If they still can’t get in, use the support email link at the bottom of this page.',
        ],
      },
    ],
  },

  {
    // Lead-assigned categories (plan 2026-10-28-001, R4–R8). Grid at
    // Projects & tasks → General activities; team checkbox = whole team
    // (joiners included); a person's tick applies on every team they're on;
    // team off leaves people who get it through another team.
    id: 'approver-general-activities',
    tier: 'approver',
    question: 'How do I choose who on my team gets a General Activity?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Open ',
          { strong: 'Projects & tasks → General activities' },
          '. Each column is an activity an administrator has set to ',
          { strong: 'Assigned by leads' },
          ', and each row is someone on your team. Tick or untick a person to give or remove that activity. Changes apply to timesheets not yet submitted.',
        ],
      },
      {
        kind: 'definitionList',
        items: [
          {
            term: 'The team checkbox',
            description:
              'Gives the activity to everyone on the team, including people who join later. It shows a dash when some people have been removed; clicking it then gives the activity back to everyone.',
          },
          {
            term: 'Turning a team off',
            description:
              'Click a team checkbox that is fully ticked. The confirmation shows who loses the activity, whose rows stay because they hold hours, and who keeps it through another team. To turn off a team that shows a dash, click it once to turn it on, then click it again.',
          },
          {
            term: 'People on two teams',
            description:
              'They appear under both teams, and either lead can change their activities. Ticking or unticking them applies on both teams.',
          },
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Removing an activity never deletes time. If someone already has hours or a note on it, their row stays, marked “No longer given”, and takes no new hours.',
        ],
      },
    ],
  },
  {
    // Leads only choose people; the catalog is administrator-only
    // (Administration → Categories & Classifications).
    id: 'approver-general-activities-catalog',
    tier: 'approver',
    question: 'Can I add or rename a General Activity?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'No. Administrators manage the list of activities, their names, and whether each one appears for everyone, for approvers only, or is assigned by leads. Ask an administrator if your team needs a new one.',
        ],
      },
    ],
  },

  // ── Administering the app ───────────────────────────────────────────────
  {
    // Lead-assigned categories (plan 2026-10-28-001, R3, D7): switching in
    // starts on every team; re-entering starts fresh; no-team people are
    // granted individually in the grid's No team group.
    id: 'administrator-general-activities',
    tier: 'administrator',
    question: 'How do I let team leads choose who gets a General Activity?',
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'In ',
          { strong: 'Administration → Categories & Classifications' },
          ', set the category’s ',
          { strong: 'Visible to' },
          ' to ',
          { strong: 'Assigned by leads' },
          '. It starts on for every team, so nobody loses it that day, and leads then narrow it under ',
          { strong: 'Projects & tasks → General activities' },
          '.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'People who aren’t on any team only get a lead-assigned activity if an administrator ticks them in the ',
          { strong: 'No team' },
          ' group of that grid.',
        ],
      },
      {
        kind: 'hint',
        spans: [
          'Switching a category back to Everyone or Approvers only takes effect straight away. If you later set it to Assigned by leads again, it starts fresh: leads’ earlier choices are discarded.',
        ],
      },
    ],
  },
  {
    id: 'administrator-roles',
    tier: 'administrator',
    question: 'How do I add a user and give them a role?',
    answer: [
      {
        // Corrected: there is no "Add user". The roster syncs from Azure AD
        // (REQUIREMENTS.md I-08); the only provisioning control is "Sync now"
        // — UsersPane.tsx:445.
        kind: 'paragraph',
        spans: [
          'Accounts are not created by hand — the roster syncs from Azure AD, and new people arrive as contributors. ',
          { strong: 'Administration → Users & Roles → Sync now' },
          ' runs that sync immediately instead of waiting for the nightly one.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'Roles are set per team under ',
          { strong: 'Administration → Teams' },
          ', where each member is either a contributor or an approver, so one person can contribute on one team and approve on another. Administrator is org-wide.',
        ],
      },
    ],
  },
  {
    id: 'administrator-close-period',
    tier: 'administrator',
    question: 'When should I close a reporting period?',
    answer: [
      {
        // The prototype's claim that close/reopen is recorded in the audit
        // log was false when this entry was written; it is true since #64, and
        // the statement now lives under administrator-audit-log. Its claim that
        // closing locks the weeks "for everyone" is true as of 2026-10-19 (#65):
        // approver and administrator actions are gated as well as contributor ones.
        kind: 'paragraph',
        spans: [
          'Close a period once every week inside it is approved. ',
          { strong: 'Administration → Reporting periods' },
          ' carries ',
          { strong: 'Close' },
          ' and ',
          { strong: 'Reopen' },
          ', and both are administrator-only.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'Closing locks the weeks inside the period for everyone: contributors can\u2019t submit, edit or recall them, and approvers — administrators included — can\u2019t approve, return or unapprove them. Reopening is how you make a change.',
        ],
      },
    ],
  },
  {
    id: 'administrator-change-approver',
    tier: 'administrator',
    question: "Can I change a team's approver mid-period?",
    answer: [
      {
        kind: 'paragraph',
        spans: [
          'Yes. Approver is a team-level role set per member under ',
          { strong: 'Administration → Teams' },
          ', so a team can have more than one at a time.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'Sheets already submitted stay in the queue and become visible to the new approver immediately.',
        ],
      },
    ],
  },
  {
    id: 'administrator-audit-log',
    tier: 'administrator',
    question: 'Where do I see who changed what?',
    answer: [
      {
        // Corrected: the filters are Actor, Object and Range — there is no
        // "area" filter. Reporting-period close and reopen are recorded since
        // #64 (REPORTING_PERIOD_CLOSED / _REOPENED, with the reason in the
        // event's comment; a reason is mandatory on reopen).
        kind: 'paragraph',
        spans: [
          { strong: 'Administration → Audit log' },
          '. Role changes, approvals, returns, unapprovals, recalls, and reporting periods being closed or reopened are recorded with actor and timestamp. Closing or reopening a period also records the reason given — reopening requires one.',
        ],
      },
      {
        kind: 'paragraph',
        spans: [
          'Filter by ',
          { strong: 'Actor' },
          ', ',
          { strong: 'Object' },
          ' or ',
          { strong: 'Range' },
          ' to narrow it down.',
        ],
      },
    ],
  },
];

/** Every published entry, in authoring order. */
export const FAQ_ENTRIES: readonly FaqEntry[] = FAQ_ENTRY_LIST;

/**
 * Resolve the tier groups a role should see, highest tier first.
 *
 * A question already shown by a higher tier is not repeated lower down, and a
 * group left empty by that dedupe is dropped rather than rendering a bare label
 * bar. Two entries that collide *within* one tier are an authoring mistake, and
 * both are kept: silently dropping one is how a row disappears, and their
 * distinct `id`s keep both addressable.
 *
 * Returns zero groups both when nothing is published at all and when nothing is
 * published for this role. The caller tells those apart from the entry list it
 * passed in, which is what decides the empty-state copy.
 */
export function faqGroupsFor(
  role: Role,
  entries: readonly FaqEntry[] = FAQ_ENTRIES,
): readonly FaqGroup[] {
  const seenQuestions = new Set<string>();
  const groups: FaqGroup[] = [];

  for (const tier of FAQ_TIER_ORDER[role]) {
    const tierEntries = entries.filter(
      (entry) => entry.tier === tier && !seenQuestions.has(entry.question.trim()),
    );
    // Marked seen only after the whole tier is filtered, so a same-tier
    // duplicate survives while a lower-tier repeat does not.
    for (const entry of tierEntries) seenQuestions.add(entry.question.trim());
    if (tierEntries.length > 0) {
      groups.push({ tier, label: FAQ_TIER_LABELS[tier], entries: tierEntries });
    }
  }

  return groups;
}
